import { DemoNotice } from "@portfolio-demos/ui";
import { useEffect, useRef, useState } from "react";
import { fetchScenario, resolveContact } from "./api";
import {
  type BattleOutcome,
  cellCoordinate,
  isAdjacent,
  type PublicPiece,
  type Rank,
  rankLabels,
  rankMarks,
  type Scenario,
} from "./game";

type GameStatus = "active" | "won" | "lost" | "expired";

type GameState = {
  pieces: PublicPiece[];
  commandsUsed: number;
  status: GameStatus;
  log: string[];
};

const clonePieces = (scenario: Scenario) => scenario.pieces.map((piece) => ({ ...piece }));

export const createInitialGame = (scenario: Scenario): GameState => ({
  pieces: clonePieces(scenario),
  commandsUsed: 0,
  status: "active",
  log: ["Field opened. Opposing ranks remain concealed until challenged."],
});

const rankName = (rank: Rank | null) => (rank ? rankLabels[rank] : "Unknown contact");

const statusCopy: Record<GameStatus, { eyebrow: string; title: string; detail: string }> = {
  active: {
    eyebrow: "Operation active",
    title: "Find the break in the screen.",
    detail:
      "Select a blue piece, then move one orthogonal square or challenge an adjacent contact.",
  },
  won: {
    eyebrow: "Scenario secured",
    title: "Opposing Flag captured.",
    detail: "The center line held just long enough for the breakthrough.",
  },
  lost: {
    eyebrow: "Scenario failed",
    title: "Your Flag was eliminated.",
    detail: "Reset the field and protect the command post before pressing forward.",
  },
  expired: {
    eyebrow: "Command limit reached",
    title: "The window closed.",
    detail: "Reset the field and use the battle hierarchy to make a shorter breakthrough.",
  },
};

const outcomeLog = (
  attacker: PublicPiece,
  defender: PublicPiece,
  battle: BattleOutcome,
  command: number,
) => {
  const attackerLabel = rankName(attacker.rank);
  const defenderLabel = rankLabels[battle.defenderRank];
  const result =
    battle.result === "attacker"
      ? `${attackerLabel} defeats ${defenderLabel}.`
      : battle.result === "defender"
        ? `${defenderLabel} defeats ${attackerLabel}.`
        : `${attackerLabel} and ${defenderLabel} eliminate each other.`;
  return `${String(command).padStart(2, "0")} · ${cellCoordinate(attacker.row, attacker.column)} → ${cellCoordinate(defender.row, defender.column)}. ${result}`;
};

const nextStatus = (
  battle: BattleOutcome | null,
  attacker: PublicPiece | null,
  command: number,
  limit: number,
): GameStatus => {
  if (battle?.capturesFlag) {
    return "won";
  }
  if (battle?.result === "defender" && attacker?.rank === "flag") {
    return "lost";
  }
  return command >= limit ? "expired" : "active";
};

type BoardProps = {
  pieces: readonly PublicPiece[];
  selectedId: string | null;
  disabled: boolean;
  onCell: (row: number, column: number) => void;
};

const Board = ({ pieces, selectedId, disabled, onCell }: BoardProps) => {
  const [focusIndex, setFocusIndex] = useState(47);
  const cells = useRef(new Map<number, HTMLButtonElement>());
  const selected = pieces.find((piece) => piece.id === selectedId) ?? null;

  const moveFocus = (index: number, key: string) => {
    const row = Math.floor(index / 9);
    const column = index % 9;
    const next =
      key === "ArrowUp"
        ? [Math.max(0, row - 1), column]
        : key === "ArrowDown"
          ? [Math.min(7, row + 1), column]
          : key === "ArrowLeft"
            ? [row, Math.max(0, column - 1)]
            : [row, Math.min(8, column + 1)];
    const nextIndex = (next[0] ?? row) * 9 + (next[1] ?? column);
    setFocusIndex(nextIndex);
    cells.current.get(nextIndex)?.focus();
  };

  return (
    <div className="board-frame">
      <div className="file-labels" aria-hidden="true">
        {Array.from({ length: 9 }, (_, index) => (
          <span key={String.fromCharCode(65 + index)}>{String.fromCharCode(65 + index)}</span>
        ))}
      </div>
      <fieldset className="board-group">
        <legend className="sr-only">Eight by nine tactical board</legend>
        <div className="board">
          {Array.from({ length: 72 }, (_, index) => {
            const row = Math.floor(index / 9);
            const column = index % 9;
            const coordinate = cellCoordinate(row, column);
            const piece = pieces.find(
              (candidate) => candidate.row === row && candidate.column === column,
            );
            const isSelected = piece?.id === selectedId;
            const isLegal = selected ? isAdjacent(selected, { row, column }) : false;
            const pieceLabel = piece
              ? piece.side === "player"
                ? `your ${rankName(piece.rank)}`
                : piece.revealed
                  ? `opposing ${rankName(piece.rank)}`
                  : "opposing piece, rank hidden"
              : "empty";

            return (
              <button
                key={coordinate}
                ref={(element) => {
                  if (element) {
                    cells.current.set(index, element);
                  } else {
                    cells.current.delete(index);
                  }
                }}
                type="button"
                className={`board-cell${piece ? ` board-cell--${piece.side}` : ""}${isSelected ? " board-cell--selected" : ""}${isLegal ? " board-cell--legal" : ""}`}
                aria-label={`${coordinate}, ${pieceLabel}${isSelected ? ", selected" : ""}`}
                aria-pressed={piece?.side === "player" ? isSelected : undefined}
                data-cell={`${row}-${column}`}
                disabled={disabled}
                tabIndex={index === focusIndex ? 0 : -1}
                onFocus={() => setFocusIndex(index)}
                onKeyDown={(event) => {
                  if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
                    event.preventDefault();
                    moveFocus(index, event.key);
                  }
                }}
                onClick={() => onCell(row, column)}
              >
                <span className="cell-coordinate" aria-hidden="true">
                  {coordinate}
                </span>
                {piece ? (
                  <span className="piece" data-side={piece.side} aria-hidden="true">
                    <span className="piece-mark">
                      {piece.side === "opponent" && !piece.revealed
                        ? "?"
                        : piece.rank
                          ? rankMarks[piece.rank]
                          : "?"}
                    </span>
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </fieldset>
      <p className="board-help">Arrow keys move board focus. Enter or Space selects and moves.</p>
    </div>
  );
};

export const App = () => {
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [game, setGame] = useState<GameState | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("Loading field orders…");
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchScenario(controller.signal)
      .then((nextScenario) => {
        setScenario(nextScenario);
        setGame(createInitialGame(nextScenario));
        setMessage("Field ready. Select a blue piece to issue the first command.");
        setLoadError(null);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setLoadError(error instanceof Error ? error.message : "Field orders could not be loaded.");
      });
    return () => controller.abort();
  }, []);

  const reset = () => {
    if (!scenario) {
      return;
    }
    setGame(createInitialGame(scenario));
    setSelectedId(null);
    setBusy(false);
    setMessage("Field reset. Opposing ranks are concealed again.");
  };

  const handleCell = async (row: number, column: number) => {
    if (!scenario || !game || busy || game.status !== "active") {
      return;
    }

    const destination = game.pieces.find((piece) => piece.row === row && piece.column === column);
    const selected = game.pieces.find((piece) => piece.id === selectedId) ?? null;

    if (!selected) {
      if (destination?.side === "player") {
        setSelectedId(destination.id);
        setMessage(`${rankName(destination.rank)} selected at ${cellCoordinate(row, column)}.`);
      } else if (destination?.side === "opponent") {
        setMessage("That contact is concealed. Select one of your blue pieces first.");
      } else {
        setMessage("Select one of your blue pieces before choosing a destination.");
      }
      return;
    }

    if (destination?.id === selected.id) {
      setSelectedId(null);
      setMessage(`${rankName(selected.rank)} deselected.`);
      return;
    }

    if (!isAdjacent(selected, { row, column })) {
      setMessage("Pieces move one square forward, backward, or sideways — never diagonally.");
      return;
    }

    if (destination?.side === "player") {
      setMessage("That square is occupied by your own piece.");
      return;
    }

    const command = game.commandsUsed + 1;
    setSelectedId(null);

    if (!destination) {
      const moved = game.pieces.map((piece) =>
        piece.id === selected.id ? { ...piece, row, column } : piece,
      );
      setGame({
        ...game,
        pieces: moved,
        commandsUsed: command,
        status: nextStatus(null, selected, command, scenario.commandLimit),
        log: [
          `${String(command).padStart(2, "0")} · ${rankName(selected.rank)} moved ${cellCoordinate(selected.row, selected.column)} → ${cellCoordinate(row, column)}.`,
          ...game.log,
        ],
      });
      setMessage(`${rankName(selected.rank)} advanced to ${cellCoordinate(row, column)}.`);
      return;
    }

    if (!selected.rank) {
      setMessage("The selected piece has no visible rank and cannot challenge.");
      return;
    }

    setBusy(true);
    setMessage("Arbiter resolving challenge…");
    try {
      const battle = await resolveContact(selected.rank, destination.id);
      const survivors = game.pieces
        .filter((piece) => {
          if (battle.result === "attacker") {
            return piece.id !== destination.id;
          }
          if (battle.result === "defender") {
            return piece.id !== selected.id;
          }
          return piece.id !== selected.id && piece.id !== destination.id;
        })
        .map((piece) => {
          if (battle.result === "attacker" && piece.id === selected.id) {
            return { ...piece, row, column };
          }
          if (battle.result === "defender" && piece.id === destination.id) {
            return { ...piece, rank: battle.defenderRank, revealed: true };
          }
          return piece;
        });
      const status = nextStatus(battle, selected, command, scenario.commandLimit);
      setGame({
        ...game,
        pieces: survivors,
        commandsUsed: command,
        status,
        log: [outcomeLog(selected, destination, battle, command), ...game.log],
      });
      setMessage(
        status === "won"
          ? "Scenario secured. The opposing Flag has been captured."
          : `${battle.reason} ${battle.result === "split" ? "Both pieces leave the field." : "The survivor holds the square."}`,
      );
    } catch (error) {
      setSelectedId(selected.id);
      setMessage(
        error instanceof Error ? error.message : "The arbiter could not resolve the challenge.",
      );
    } finally {
      setBusy(false);
    }
  };

  const currentStatus = statusCopy[game?.status ?? "active"];
  const commandsRemaining = scenario && game ? scenario.commandLimit - game.commandsUsed : 0;
  const blueRemaining = game?.pieces.filter((piece) => piece.side === "player").length ?? 0;
  const redRemaining = game?.pieces.filter((piece) => piece.side === "opponent").length ?? 0;

  return (
    <div className="field-manual">
      <a className="skip-link" href="#main-content">
        Skip to mission briefing
      </a>
      <header className="masthead">
        <a className="wordmark" href="/" aria-label="Field Manual home">
          <span>Field</span>
          <strong>Manual 01</strong>
        </a>
        <p>Game of the Generals · tactical training scenario</p>
      </header>

      <main id="main-content">
        <DemoNotice title="Synthetic tabletop exercise">
          <p>
            Fictional forces only. This focused scenario demonstrates hidden ranks, orthogonal
            movement, battle arbitration, and replay history; it does not save matches or player
            data.
          </p>
        </DemoNotice>

        <section className="briefing" aria-labelledby="mission-title">
          <div>
            <p className="kicker">Training file / FM–01</p>
            <h1 id="mission-title">{scenario?.title ?? "Receiving field orders"}</h1>
          </div>
          <p className="briefing-copy">
            {scenario?.briefing ?? "A deterministic training scenario is being prepared."}
          </p>
        </section>

        {loadError ? (
          <section className="load-failure" role="alert">
            <p className="kicker">Field link unavailable</p>
            <h2>Orders could not be received.</h2>
            <p>{loadError}</p>
            <button type="button" onClick={() => window.location.reload()}>
              Retry connection
            </button>
          </section>
        ) : null}

        {scenario && game ? (
          <>
            <dl className="status-ledger" aria-label="Scenario status">
              <div>
                <dt>Commands left</dt>
                <dd>{commandsRemaining}</dd>
              </div>
              <div>
                <dt>Blue on field</dt>
                <dd>{blueRemaining}</dd>
              </div>
              <div>
                <dt>Red contacts</dt>
                <dd>{redRemaining}</dd>
              </div>
              <div>
                <dt>Arbiter</dt>
                <dd>{busy ? "Resolving" : "Ready"}</dd>
              </div>
            </dl>

            <div className="operations-layout">
              <section className="battlefield-section" aria-labelledby="battlefield-title">
                <div className="section-heading">
                  <div>
                    <p className="kicker">Tactical board / north is up</p>
                    <h2 id="battlefield-title">Break the center line</h2>
                  </div>
                  <fieldset className="force-key">
                    <legend className="sr-only">Piece ownership legend</legend>
                    <span>
                      <i data-force="blue" /> Your force
                    </span>
                    <span>
                      <i data-force="red" /> Hidden contact
                    </span>
                  </fieldset>
                </div>
                <Board
                  pieces={game.pieces}
                  selectedId={selectedId}
                  disabled={busy || game.status !== "active"}
                  onCell={handleCell}
                />
              </section>

              <aside className="field-notes" aria-label="Mission controls and field notes">
                <section className={`mission-state mission-state--${game.status}`}>
                  <p className="kicker">{currentStatus.eyebrow}</p>
                  <h2>{currentStatus.title}</h2>
                  <p>{currentStatus.detail}</p>
                  <p className="live-message" aria-live="polite">
                    {message}
                  </p>
                  <button type="button" className="reset-button" onClick={reset}>
                    Reset scenario
                  </button>
                </section>

                <section className="doctrine" aria-labelledby="doctrine-title">
                  <p className="kicker">Pocket doctrine</p>
                  <h2 id="doctrine-title">Challenge hierarchy</h2>
                  <ul>
                    <li>
                      <strong>Officers</strong> defeat lower officers and Privates.
                    </li>
                    <li>
                      <strong>Spies</strong> defeat officers; Privates defeat Spies.
                    </li>
                    <li>
                      <strong>Equal ranks</strong> both leave the field.
                    </li>
                    <li>
                      <strong>Any piece</strong> can capture the opposing Flag.
                    </li>
                  </ul>
                </section>

                <section className="action-log" aria-labelledby="log-title">
                  <div className="log-heading">
                    <div>
                      <p className="kicker">Arbiter record</p>
                      <h2 id="log-title">Replay log</h2>
                    </div>
                    <span>{game.log.length} entries</span>
                  </div>
                  <ol aria-live="polite">
                    {game.log.map((entry) => (
                      <li key={entry}>{entry}</li>
                    ))}
                  </ol>
                </section>
              </aside>
            </div>
          </>
        ) : null}
      </main>

      <footer>
        <p>Portfolio demonstration · deterministic synthetic scenario · no saved state</p>
        <a href="https://jericop.dev">Return to jericop.dev</a>
      </footer>
    </div>
  );
};
